import { ServiceDefinition, ServiceCategory } from '../types/configurator';

// Extended category type for AI services
export type ExtendedServiceCategory = ServiceCategory | 'ai' | 'rendering';

export const SERVICES: ServiceDefinition[] = [
  // Infrastructure Services
  {
    id: 'dns',
    name: 'DNS Resolver',
    description: 'Local DNS server (dnsmasq) for *.hermit.local resolution',
    category: 'infrastructure',
    port: 53,
    requiredNodeTypes: ['gateway'],
    critical: true,
    defaultEnabled: true,
    memoryMB: 32,
    cpuCores: 0.1,
  },
  {
    id: 'dhcp',
    name: 'DHCP Server',
    description: 'Dynamic IP allocation and PXE boot support',
    category: 'infrastructure',
    port: 67,
    requiredNodeTypes: ['gateway'],
    critical: true,
    defaultEnabled: true,
    memoryMB: 16,
    cpuCores: 0.1,
  },
  {
    id: 'pxe',
    name: 'PXE Boot Server',
    description: 'Network boot for bare-metal provisioning (TFTP + HTTP)',
    category: 'infrastructure',
    port: 69,
    requiredNodeTypes: ['gateway'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 64,
    cpuCores: 0.2,
  },
  {
    id: 'wireguard',
    name: 'WireGuard VPN',
    description: 'Secure remote access tunnel for operators',
    category: 'infrastructure',
    port: 51820,
    requiredNodeTypes: ['gateway'],
    critical: true,
    defaultEnabled: true,
    memoryMB: 16,
    cpuCores: 0.1,
  },
  {
    id: 'ntp',
    name: 'NTP Server',
    description: 'Network time synchronization for all nodes',
    category: 'infrastructure',
    port: 123,
    requiredNodeTypes: ['gateway'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 8,
    cpuCores: 0.05,
  },

  // Storage Services
  {
    id: 'registry',
    name: 'Container Registry',
    description: 'Local Docker/OCI image registry',
    category: 'storage',
    port: 5000,
    requiredNodeTypes: ['gateway', 'storage', 'compute'],
    critical: true,
    defaultEnabled: true,
    memoryMB: 256,
    cpuCores: 0.5,
  },
  {
    id: 'minio',
    name: 'MinIO Object Storage',
    description: 'S3-compatible object storage for datasets and artifacts',
    category: 'storage',
    port: 9000,
    requiredNodeTypes: ['storage', 'compute'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 512,
    cpuCores: 1,
  },
  {
    id: 'nfs',
    name: 'NFS File Server',
    description: 'Network file system for shared storage',
    category: 'storage',
    port: 2049,
    requiredNodeTypes: ['storage'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 128,
    cpuCores: 0.3,
  },

  // Monitoring Services
  {
    id: 'prometheus',
    name: 'Prometheus',
    description: 'Metrics collection and time-series database',
    category: 'monitoring',
    port: 9090,
    requiredNodeTypes: ['gateway', 'compute'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 256,
    cpuCores: 0.5,
  },
  {
    id: 'grafana',
    name: 'Grafana',
    description: 'Metrics visualization and dashboards',
    category: 'monitoring',
    port: 3000,
    requiredNodeTypes: ['gateway', 'compute'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 256,
    cpuCores: 0.5,
  },
  {
    id: 'loki',
    name: 'Loki',
    description: 'Log aggregation and querying',
    category: 'monitoring',
    port: 3100,
    requiredNodeTypes: ['gateway', 'compute', 'storage'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 512,
    cpuCores: 0.5,
  },
  {
    id: 'node-exporter',
    name: 'Node Exporter',
    description: 'Hardware and OS metrics exporter',
    category: 'monitoring',
    port: 9100,
    requiredNodeTypes: ['gateway', 'compute', 'storage', 'daq'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 32,
    cpuCores: 0.1,
  },

  // Workload Services
  {
    id: 'portainer',
    name: 'Portainer',
    description: 'Docker container management UI',
    category: 'workload',
    port: 9443,
    requiredNodeTypes: ['gateway', 'compute'],
    critical: false,
    defaultEnabled: true,
    memoryMB: 128,
    cpuCores: 0.3,
  },
  {
    id: 'traefik',
    name: 'Traefik',
    description: 'Reverse proxy and load balancer',
    category: 'workload',
    port: 80,
    requiredNodeTypes: ['gateway', 'compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 64,
    cpuCores: 0.2,
  },
  {
    id: 'jupyter',
    name: 'JupyterHub',
    description: 'Multi-user Jupyter notebook server',
    category: 'workload',
    port: 8000,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 1024,
    cpuCores: 2,
  },
  {
    id: 'gitea',
    name: 'Gitea',
    description: 'Lightweight Git server',
    category: 'workload',
    port: 3001,
    requiredNodeTypes: ['compute', 'storage'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 256,
    cpuCores: 0.5,
  },
  {
    id: 'influxdb',
    name: 'InfluxDB',
    description: 'Time-series database for sensor data',
    category: 'storage',
    port: 8086,
    requiredNodeTypes: ['storage', 'compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 512,
    cpuCores: 1,
  },
  {
    id: 'telegraf',
    name: 'Telegraf',
    description: 'Metrics collection agent for DAQ systems',
    category: 'monitoring',
    port: 8094,
    requiredNodeTypes: ['daq', 'compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 64,
    cpuCores: 0.2,
  },

  // AI/LLM Services
  {
    id: 'ollama',
    name: 'Ollama',
    description: 'Run LLMs locally with easy model management. Supports Llama, Mistral, and more.',
    category: 'ai',
    port: 11434,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 8192,
    cpuCores: 4,
  },
  {
    id: 'localai',
    name: 'LocalAI',
    description: 'OpenAI-compatible API for local LLM inference. Self-hosted alternative to ChatGPT.',
    category: 'ai',
    port: 8080,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 16384,
    cpuCores: 8,
  },
  {
    id: 'text-generation-webui',
    name: 'Text Generation WebUI',
    description: 'Gradio web UI for running Large Language Models with many backends.',
    category: 'ai',
    port: 7860,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 16384,
    cpuCores: 4,
  },
  {
    id: 'open-webui',
    name: 'Open WebUI',
    description: 'ChatGPT-style interface for Ollama. User-friendly LLM chat frontend.',
    category: 'ai',
    port: 3000,
    requiredNodeTypes: ['compute', 'gateway'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 512,
    cpuCores: 0.5,
  },
  {
    id: 'langchain-serve',
    name: 'LangChain Serve',
    description: 'Deploy LangChain chains and agents as REST APIs.',
    category: 'ai',
    port: 8000,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 2048,
    cpuCores: 2,
  },

  // AI Rendering Services
  {
    id: 'comfyui',
    name: 'ComfyUI',
    description: 'Node-based Stable Diffusion interface. Advanced workflows for AI image generation.',
    category: 'rendering',
    port: 8188,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 8192,
    cpuCores: 4,
  },
  {
    id: 'automatic1111',
    name: 'Stable Diffusion WebUI',
    description: 'AUTOMATIC1111 web interface for Stable Diffusion. Industry standard for AI art.',
    category: 'rendering',
    port: 7861,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 8192,
    cpuCores: 4,
  },
  {
    id: 'invokeai',
    name: 'InvokeAI',
    description: 'Professional creative AI toolkit for image generation with advanced features.',
    category: 'rendering',
    port: 9090,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 8192,
    cpuCores: 4,
  },
  {
    id: 'kohya-ss',
    name: 'Kohya SS',
    description: 'LoRA and fine-tuning GUI for Stable Diffusion models.',
    category: 'rendering',
    port: 7862,
    requiredNodeTypes: ['compute'],
    critical: false,
    defaultEnabled: false,
    memoryMB: 16384,
    cpuCores: 8,
  },
];

export function getServiceById(id: string): ServiceDefinition | undefined {
  return SERVICES.find((s) => s.id === id);
}

export function getServicesByCategory(category: string): ServiceDefinition[] {
  return SERVICES.filter((s) => s.category === category);
}

export function getRequiredServices(): ServiceDefinition[] {
  return SERVICES.filter((s) => s.critical);
}

export function getDefaultServices(): ServiceDefinition[] {
  return SERVICES.filter((s) => s.defaultEnabled);
}

export function canServiceRunOnNode(serviceId: string, nodeType: string): boolean {
  const service = getServiceById(serviceId);
  if (!service) return false;
  return service.requiredNodeTypes.includes(nodeType as never);
}
